import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { useLanguage } from '../../contexts/LanguageContext';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Checkbox } from '../../components/ui/checkbox';
import { toast } from 'sonner';
import {
  Users,
  ClipboardList,
  Search,
  CheckCircle,
  Clock,
  Send,
  User,
  Mail,
  Phone,
  ChevronUp,
  TrendingUp,
  BarChart3,
  PieChart,
  Activity,
  Target,
  Calendar
} from 'lucide-react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  PieChart as RechartsPieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  CartesianGrid,
  Legend
} from 'recharts';

const API = process.env.REACT_APP_BACKEND_URL;

// Colors for charts
const CHART_COLORS = ['#2563eb', '#7c3aed', '#06b6d4', '#10b981', '#f59e0b', '#ef4444', '#ec4899'];
const CATEGORY_COLORS = {
  depression: '#3b82f6',
  anxiety: '#f43f5e',
  stress: '#f59e0b',
  comprehensive: '#8b5cf6',
  wellbeing: '#10b981',
  distress: '#f97316',
  'self-esteem': '#06b6d4'
};

const SpecialistDashboard = () => {
  const { language } = useLanguage();
  const { token, user } = useAuth();
  const navigate = useNavigate();
  
  const [clients, setClients] = useState([]);
  const [tests, setTests] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedClient, setSelectedClient] = useState(null);
  const [selectedTests, setSelectedTests] = useState([]);
  const [assigning, setAssigning] = useState(false);
  const [sendNotification, setSendNotification] = useState(true);
  const [activeTab, setActiveTab] = useState('overview'); // 'overview' or 'assign'

  useEffect(() => {
    if (user?.role !== 'specialist') {
      toast.error('Accesso riservato agli specialisti');
      navigate('/dashboard');
      return;
    }
    fetchData();
  }, [user, navigate]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [clientsRes, testsRes, statsRes] = await Promise.all([
        axios.get(`${API}/api/specialist/clients`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/api/tests?language=${language}`),
        axios.get(`${API}/api/specialist/stats`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      
      setClients(clientsRes.data.clients || []);
      setTests(testsRes.data || []);
      setStats(statsRes.data || null);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Errore nel caricamento dei dati');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectClient = (client) => {
    setSelectedClient(client);
    setSelectedTests(client.assigned_tests || []);
    setActiveTab('assign');
  };

  const handleToggleTest = (testId) => {
    setSelectedTests(prev => 
      prev.includes(testId) 
        ? prev.filter(id => id !== testId)
        : [...prev, testId]
    );
  };

  const handleSelectAllTests = () => {
    if (selectedTests.length === tests.length) {
      setSelectedTests([]);
    } else {
      setSelectedTests(tests.map(t => t.id));
    }
  };

  const handleAssignTests = async () => {
    if (!selectedClient || selectedTests.length === 0) {
      toast.error('Seleziona almeno un test da assegnare');
      return;
    }

    setAssigning(true);
    try {
      await axios.post(
        `${API}/api/specialist/assign-tests`,
        {
          client_id: selectedClient.id,
          test_ids: selectedTests,
          send_notification: sendNotification
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(`${selectedTests.length} test assegnati a ${selectedClient.name}`);
      fetchData();
      setSelectedClient(null);
      setSelectedTests([]);
      setActiveTab('overview');
    } catch (error) {
      console.error('Error assigning tests:', error);
      toast.error('Errore nell\'assegnazione dei test');
    } finally {
      setAssigning(false);
    }
  };

  const filteredClients = clients.filter(client => 
    client.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    client.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    client.surname?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const getCompletionStatus = (client) => {
    const assigned = client.assigned_tests?.length || 0;
    const completed = client.tests_completed?.filter(id => 
      client.assigned_tests?.includes(id)
    ).length || 0;
    
    if (assigned === 0) return { status: 'no_tests', label: 'Nessun test', color: 'bg-gray-100 text-gray-600' };
    if (completed === 0) return { status: 'not_started', label: 'Non iniziato', color: 'bg-yellow-100 text-yellow-700' };
    if (completed < assigned) return { status: 'in_progress', label: `${completed}/${assigned}`, color: 'bg-blue-100 text-blue-700' };
    return { status: 'completed', label: 'Completato', color: 'bg-green-100 text-green-700' };
  };

  // Custom tooltip for charts
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white p-3 rounded-xl shadow-lg border">
          <p className="font-medium text-sm">{label}</p>
          {payload.map((entry, index) => (
            <p key={index} className="text-sm" style={{ color: entry.color }}>
              {entry.name}: {entry.value}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-7xl mx-auto" data-testid="specialist-dashboard">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-primary">Dashboard Specialista</h1>
          <p className="text-muted-foreground">Panoramica e gestione pazienti</p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant={activeTab === 'overview' ? 'default' : 'outline'}
            onClick={() => setActiveTab('overview')}
            className="rounded-full"
            size="sm"
          >
            <BarChart3 className="w-4 h-4 mr-2" />
            Statistiche
          </Button>
          <Button
            variant={activeTab === 'assign' ? 'default' : 'outline'}
            onClick={() => setActiveTab('assign')}
            className="rounded-full"
            size="sm"
          >
            <ClipboardList className="w-4 h-4 mr-2" />
            Assegna Test
          </Button>
        </div>
      </div>

      {/* Stats Summary Cards */}
      {stats && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="grid grid-cols-2 lg:grid-cols-4 gap-4"
        >
          <Card className="border-0 shadow-md rounded-2xl bg-gradient-to-br from-blue-500 to-blue-600 text-white">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-blue-100 text-sm">Pazienti Totali</p>
                  <p className="text-3xl font-bold">{stats.summary.total_clients}</p>
                </div>
                <Users className="w-10 h-10 text-blue-200" />
              </div>
            </CardContent>
          </Card>

          <Card className="border-0 shadow-md rounded-2xl bg-gradient-to-br from-purple-500 to-purple-600 text-white">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-purple-100 text-sm">Test Assegnati</p>
                  <p className="text-3xl font-bold">{stats.summary.total_assigned}</p>
                </div>
                <ClipboardList className="w-10 h-10 text-purple-200" />
              </div>
            </CardContent>
          </Card>

          <Card className="border-0 shadow-md rounded-2xl bg-gradient-to-br from-emerald-500 to-emerald-600 text-white">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-emerald-100 text-sm">Test Completati</p>
                  <p className="text-3xl font-bold">{stats.summary.total_completed}</p>
                </div>
                <CheckCircle className="w-10 h-10 text-emerald-200" />
              </div>
            </CardContent>
          </Card>

          <Card className="border-0 shadow-md rounded-2xl bg-gradient-to-br from-amber-500 to-amber-600 text-white">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-amber-100 text-sm">Tasso Completamento</p>
                  <p className="text-3xl font-bold">{stats.summary.completion_rate}%</p>
                </div>
                <Target className="w-10 h-10 text-amber-200" />
              </div>
            </CardContent>
          </Card>
        </motion.div>
      )}

      {/* Main Content Area */}
      {activeTab === 'overview' ? (
        /* Statistics View */
        <div className="grid lg:grid-cols-2 gap-6">
          {/* Monthly Trend Chart */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
          >
            <Card className="border-0 shadow-lg rounded-2xl">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg">
                  <TrendingUp className="w-5 h-5 text-blue-500" />
                  Trend Completamenti (6 mesi)
                </CardTitle>
              </CardHeader>
              <CardContent>
                {stats?.monthly_trend && stats.monthly_trend.length > 0 ? (
                  <ResponsiveContainer width="100%" height={250}>
                    <BarChart data={stats.monthly_trend}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                      <XAxis dataKey="month" stroke="#6b7280" fontSize={12} />
                      <YAxis stroke="#6b7280" fontSize={12} />
                      <Tooltip content={<CustomTooltip />} />
                      <Bar 
                        dataKey="completati" 
                        fill="#2563eb" 
                        radius={[8, 8, 0, 0]}
                        name="Test Completati"
                      />
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="h-[250px] flex items-center justify-center text-muted-foreground">
                    <p>Nessun dato disponibile</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </motion.div>

          {/* Category Distribution Chart */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            <Card className="border-0 shadow-lg rounded-2xl">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg">
                  <PieChart className="w-5 h-5 text-purple-500" />
                  Distribuzione per Categoria
                </CardTitle>
              </CardHeader>
              <CardContent>
                {stats?.category_distribution && stats.category_distribution.length > 0 ? (
                  <ResponsiveContainer width="100%" height={250}>
                    <RechartsPieChart>
                      <Pie
                        data={stats.category_distribution}
                        cx="50%"
                        cy="50%"
                        innerRadius={60}
                        outerRadius={90}
                        paddingAngle={2}
                        dataKey="value"
                        label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                        labelLine={false}
                      >
                        {stats.category_distribution.map((entry, index) => (
                          <Cell 
                            key={`cell-${index}`} 
                            fill={CATEGORY_COLORS[entry.category] || CHART_COLORS[index % CHART_COLORS.length]} 
                          />
                        ))}
                      </Pie>
                      <Tooltip />
                    </RechartsPieChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="h-[250px] flex items-center justify-center text-muted-foreground">
                    <p>Nessun test completato</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </motion.div>

          {/* Client Status Breakdown */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
          >
            <Card className="border-0 shadow-lg rounded-2xl">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg">
                  <Users className="w-5 h-5 text-emerald-500" />
                  Stato Pazienti
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="flex items-center justify-between p-3 bg-emerald-50 rounded-xl">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center">
                        <CheckCircle className="w-5 h-5 text-emerald-600" />
                      </div>
                      <div>
                        <p className="font-medium text-emerald-900">Attivi</p>
                        <p className="text-xs text-emerald-600">Hanno completato almeno 1 test</p>
                      </div>
                    </div>
                    <span className="text-2xl font-bold text-emerald-600">{stats?.client_status?.active || 0}</span>
                  </div>

                  <div className="flex items-center justify-between p-3 bg-amber-50 rounded-xl">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
                        <Clock className="w-5 h-5 text-amber-600" />
                      </div>
                      <div>
                        <p className="font-medium text-amber-900">In Attesa</p>
                        <p className="text-xs text-amber-600">Test assegnati, non iniziati</p>
                      </div>
                    </div>
                    <span className="text-2xl font-bold text-amber-600">{stats?.client_status?.pending || 0}</span>
                  </div>

                  <div className="flex items-center justify-between p-3 bg-gray-50 rounded-xl">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center">
                        <User className="w-5 h-5 text-gray-600" />
                      </div>
                      <div>
                        <p className="font-medium text-gray-900">Senza Test</p>
                        <p className="text-xs text-gray-600">Nessun test assegnato</p>
                      </div>
                    </div>
                    <span className="text-2xl font-bold text-gray-600">{stats?.client_status?.no_tests || 0}</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>

          {/* Recent Activity */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
          >
            <Card className="border-0 shadow-lg rounded-2xl">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg">
                  <Activity className="w-5 h-5 text-rose-500" />
                  Attività Recente
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <div className="flex items-center gap-4 p-3 bg-blue-50 rounded-xl">
                    <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center">
                      <Calendar className="w-6 h-6 text-blue-600" />
                    </div>
                    <div className="flex-1">
                      <p className="font-medium text-blue-900">Ultimi 7 giorni</p>
                      <p className="text-sm text-blue-600">Test completati questa settimana</p>
                    </div>
                    <span className="text-3xl font-bold text-blue-600">{stats?.summary?.recent_completions || 0}</span>
                  </div>

                  <div className="pt-2">
                    <p className="text-sm text-muted-foreground mb-3">Ultimi pazienti attivi:</p>
                    <div className="space-y-2">
                      {clients
                        .filter(c => c.tests_completed?.length > 0)
                        .slice(0, 3)
                        .map(client => (
                          <div key={client.id} className="flex items-center gap-3 p-2 bg-slate-50 rounded-lg">
                            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 to-purple-500 flex items-center justify-center text-white text-sm font-bold">
                              {client.name?.charAt(0) || 'U'}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="font-medium text-sm truncate">{client.name} {client.surname || ''}</p>
                              <p className="text-xs text-muted-foreground truncate">{client.email}</p>
                            </div>
                            <Badge className="bg-green-100 text-green-700 text-xs">
                              {client.tests_completed?.length || 0} test
                            </Badge>
                          </div>
                        ))}
                      {clients.filter(c => c.tests_completed?.length > 0).length === 0 && (
                        <p className="text-center text-muted-foreground text-sm py-4">
                          Nessun paziente ha completato test
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </div>
      ) : (
        /* Test Assignment View */
        <div className="grid lg:grid-cols-3 gap-6">
          {/* Clients List */}
          <div className="lg:col-span-1">
            <Card className="border-0 shadow-lg rounded-2xl">
              <CardHeader className="pb-4">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Users className="w-5 h-5" />
                  Lista Clienti
                </CardTitle>
                <div className="relative mt-3">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
                  <Input
                    placeholder="Cerca cliente..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10 rounded-xl"
                    data-testid="client-search-input"
                  />
                </div>
              </CardHeader>
              <CardContent className="max-h-[500px] overflow-y-auto">
                {filteredClients.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <Users className="w-12 h-12 mx-auto mb-3 opacity-30" />
                    <p>Nessun cliente trovato</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {filteredClients.map((client) => {
                      const status = getCompletionStatus(client);
                      return (
                        <motion.div
                          key={client.id}
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          className={`p-3 rounded-xl cursor-pointer transition-all ${
                            selectedClient?.id === client.id
                              ? 'bg-primary/10 border-2 border-primary'
                              : 'bg-slate-50 hover:bg-slate-100 border-2 border-transparent'
                          }`}
                          onClick={() => handleSelectClient(client)}
                          data-testid={`client-card-${client.id}`}
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex-1 min-w-0">
                              <p className="font-medium text-primary truncate">
                                {client.name} {client.surname || ''}
                              </p>
                              <p className="text-xs text-muted-foreground truncate">
                                {client.email}
                              </p>
                            </div>
                            <Badge className={`text-xs ${status.color}`}>
                              {status.label}
                            </Badge>
                          </div>
                        </motion.div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Test Assignment Panel */}
          <div className="lg:col-span-2">
            {selectedClient ? (
              <Card className="border-0 shadow-lg rounded-2xl">
                <CardHeader className="border-b">
                  <div className="flex items-start justify-between">
                    <div>
                      <CardTitle className="text-xl">
                        {selectedClient.name} {selectedClient.surname || ''}
                      </CardTitle>
                      <div className="flex flex-wrap gap-3 mt-2 text-sm text-muted-foreground">
                        <span className="flex items-center gap-1">
                          <Mail className="w-4 h-4" />
                          {selectedClient.email}
                        </span>
                        {selectedClient.phone && (
                          <span className="flex items-center gap-1">
                            <Phone className="w-4 h-4" />
                            {selectedClient.phone}
                          </span>
                        )}
                      </div>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        setSelectedClient(null);
                        setActiveTab('overview');
                      }}
                    >
                      <ChevronUp className="w-4 h-4" />
                    </Button>
                  </div>
                </CardHeader>
                <CardContent className="pt-6">
                  {/* Test Selection */}
                  <div className="mb-6">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="font-semibold flex items-center gap-2">
                        <ClipboardList className="w-5 h-5" />
                        Seleziona Test da Assegnare
                      </h3>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handleSelectAllTests}
                        className="rounded-full"
                      >
                        {selectedTests.length === tests.length ? 'Deseleziona tutti' : 'Seleziona tutti'}
                      </Button>
                    </div>
                    
                    <div className="grid sm:grid-cols-2 gap-2 max-h-[300px] overflow-y-auto pr-2">
                      {tests.map((test) => {
                        const isSelected = selectedTests.includes(test.id);
                        const isCompleted = selectedClient.tests_completed?.includes(test.id);
                        
                        return (
                          <div
                            key={test.id}
                            className={`p-3 rounded-xl border-2 cursor-pointer transition-all ${
                              isSelected
                                ? 'border-primary bg-primary/5'
                                : 'border-slate-200 hover:border-slate-300'
                            } ${isCompleted ? 'opacity-60' : ''}`}
                            onClick={() => handleToggleTest(test.id)}
                            data-testid={`test-card-${test.id}`}
                          >
                            <div className="flex items-start gap-3">
                              <Checkbox
                                checked={isSelected}
                                className="mt-1"
                              />
                              <div className="flex-1 min-w-0">
                                <p className="font-medium text-sm truncate">
                                  {test.name}
                                </p>
                                <p className="text-xs text-muted-foreground">
                                  {test.category}
                                </p>
                                {isCompleted && (
                                  <Badge className="mt-1 bg-green-100 text-green-700 text-xs">
                                    <CheckCircle className="w-3 h-3 mr-1" />
                                    Completato
                                  </Badge>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {/* Assignment Actions */}
                  <div className="border-t pt-4">
                    <div className="flex items-center gap-3 mb-4">
                      <Checkbox
                        id="sendNotification"
                        checked={sendNotification}
                        onCheckedChange={setSendNotification}
                      />
                      <label htmlFor="sendNotification" className="text-sm cursor-pointer">
                        Invia email di notifica al cliente
                      </label>
                    </div>
                    
                    <div className="flex items-center justify-between">
                      <p className="text-sm text-muted-foreground">
                        <span className="font-semibold text-primary">{selectedTests.length}</span> test selezionati
                      </p>
                      <Button
                        onClick={handleAssignTests}
                        disabled={selectedTests.length === 0 || assigning}
                        className="rounded-full bg-primary"
                        data-testid="assign-tests-button"
                      >
                        {assigning ? (
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                        ) : (
                          <Send className="w-4 h-4 mr-2" />
                        )}
                        Assegna Test
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ) : (
              <Card className="border-0 shadow-lg rounded-2xl h-full flex items-center justify-center min-h-[400px]">
                <div className="text-center p-8">
                  <User className="w-16 h-16 mx-auto mb-4 text-muted-foreground/30" />
                  <h3 className="text-lg font-semibold text-muted-foreground mb-2">
                    Seleziona un Cliente
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    Clicca su un cliente dalla lista per assegnare i test
                  </p>
                </div>
              </Card>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default SpecialistDashboard;
